/* Drawer controls delegate on document so AJAX item replacement keeps them working. */
(() => {
  const drawer = () => document.querySelector('.cart-content');
  let busy = false;
  let noteTimer;
  let refreshTimer;
  const itemsList = drawer()?.querySelector('[udy-el="items-list"]');
  if (itemsList) new MutationObserver(() => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;
        const cart = await response.json();
        window.updateFreeShippingBar?.(cart);
        window.updateCartUpsell?.(cart);
        window.syncGiftNoteUI?.(cart);
      } catch (_) { /* Ordinary cart controls remain available if recommendations fail. */ }
    }, 50);
  }).observe(itemsList, { childList: true });

  document.addEventListener('keydown', event => {
    const overlay = document.querySelector('.cart-wrapper');
    if (event.key !== 'Escape' || !overlay || getComputedStyle(overlay).display === 'none') return;
    overlay.querySelector('[data-node-type="commerce-cart-close-link"]')?.click();
    overlay.style.display = 'none';
    document.body.classList.remove('show-modal');
    document.body.classList.add('hidden-modal');
    document.getElementById('cartOpen')?.focus();
  });

  async function renderCart(cart) {
    const root = drawer();
    if (!root) return;
    if (window.cartSubscribeData) {
      await Promise.all((cart.items || []).map(async item => {
        if (window.cartSubscribeData[item.variant_id] || !item.url) return;
        try {
          const response = await fetch(item.url.split('?')[0] + '.js');
          if (!response.ok) return;
          const product = await response.json();
          const variant = product.variants.find(v => v.id === item.variant_id);
          const names = {};
          (product.selling_plan_groups || []).forEach(group => group.selling_plans.forEach(plan => { names[plan.id] = plan.name; }));
          window.cartSubscribeData[item.variant_id] = (variant?.selling_plan_allocations || []).map(plan => ({
            id: plan.selling_plan_id, name: names[plan.selling_plan_id] || '',
            price: plan.price, compare_at_price: plan.compare_at_price
          }));
        } catch (_) { /* A product without plans still has ordinary cart controls. */ }
      }));
    }
    const list = root.querySelector('[udy-el="items-list"]');
    if (list && window.udeslyMiniCartTemplate) {
      list.innerHTML = cart.items.map((item, index) => window.udeslyMiniCartTemplate({
        ...item, line: index + 1, remove: '/cart/change?line=' + (index + 1) + '&quantity=0'
      })).join('');
    }
    document.querySelectorAll('[udy-el="cart-count"]').forEach(el => { el.textContent = cart.item_count; });
    root.querySelectorAll('[udy-el="cart-total"]').forEach(el => { el.textContent = '£' + (cart.total_price / 100).toFixed(2); });
    const empty = !cart.item_count;
    root.querySelector('[data-node-type="commerce-cart-form"]')?.classList.toggle('udesly-display-none', empty);
    root.querySelector('.cart-footer-main')?.classList.toggle('udesly-display-none', empty);
    const emptyState = root.querySelector('[udy-el="no-items-in-cart"]');
    if (emptyState) {
      emptyState.classList.toggle('udesly-display-none', !empty);
      emptyState.style.display = empty ? '' : 'none';
    }
    if (window.updateFreeShippingBar) window.updateFreeShippingBar(cart);
    if (window.updateCartUpsell) window.updateCartUpsell(cart);
    if (!cart.items.some(item => String(item.variant_id) === String(window.giftNoteVariantId))) window.resetGiftNoteUI?.();
  }

  async function changeLine(line, quantity, control) {
    if (busy) return;
    busy = true;
    const root = drawer();
    root?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update your cart.');
      await renderCart(cart);
    } catch (error) {
      const box = root?.querySelector('[data-node-type="commerce-cart-error"]');
      if (box) { box.classList.remove('udesly-display-none'); box.textContent = error.message; }
      console.error('[cart-controls]', error.message);
    } finally {
      root?.removeAttribute('aria-busy');
      busy = false;
    }
  }

  document.addEventListener('click', event => {
    const checkout = event.target.closest('.cart-content #checkout');
    if (checkout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || checkout.disabled) return;
      clearTimeout(noteTimer);
      checkout.disabled = true;
      const note = drawer()?.querySelector('#cart-note')?.value;
      (async () => {
        try {
          if (note !== undefined) {
            const response = await fetch('/cart/update.js', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note })
            });
            if (!response.ok) throw new Error('Unable to save the order note.');
          }
          window.location.assign('/checkout');
        } catch (error) {
          checkout.disabled = false;
          console.error('[cart-controls]', error.message);
        }
      })();
      return;
    }
    const control = event.target.closest('.cart-content [udy-el="remove-item"], .cart-content [udy-el="quantity-plus"], .cart-content [udy-el="quantity-minus"]');
    if (!control) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const row = control.closest('li');
    const input = row?.querySelector('input[data-line]');
    const remove = control.matches('[udy-el="remove-item"]');
    const line = remove ? Number(new URL(control.href).searchParams.get('line')) : Number(input?.dataset.line);
    const quantity = remove ? 0 : Math.max(1, Number(input.value) + (control.matches('[udy-el="quantity-plus"]') ? 1 : -1));
    if (line) changeLine(line, quantity, control);
  }, true);

  document.addEventListener('change', event => {
    if (!event.target.matches('.cart-content input[type="number"][data-line]')) return;
    event.stopImmediatePropagation();
    changeLine(Number(event.target.dataset.line), Math.max(1, Number(event.target.value) || 1), event.target);
  }, true);

  document.addEventListener('input', event => {
    if (!event.target.matches('#cart-note')) return;
    clearTimeout(noteTimer);
    const note = event.target.value;
    noteTimer = setTimeout(async () => {
      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note })
        });
        if (!response.ok) throw new Error('Unable to save the order note.');
      } catch (error) { console.error('[cart-controls]', error.message); }
    }, 350);
  });

  window.refreshCartDrawer = renderCart;
})();
